// Lightweight Markdown renderer for question prompts.
// Supports: paragraphs, headings (#, ##, ###), **bold**, *italic*, `inline code`,
// fenced code blocks (```), bulleted lists (- ), and GitHub-style pipe tables.
// Intentionally small — questions are author-controlled.

export function renderPromptToHtml(src: string): string {
  const lines = src.split(/\r?\n/);
  let html = '';
  let i = 0;
  let inFence = false;
  let fenceLang = '';
  let paraBuf: string[] = [];
  let listBuf: string[] = [];

  const flushPara = () => {
    if (paraBuf.length === 0) return;
    const text = paraBuf.join(' ').trim();
    if (text) html += `<p>${inline(text)}</p>`;
    paraBuf = [];
  };

  const flushList = () => {
    if (listBuf.length === 0) return;
    html += '<ul>';
    for (const item of listBuf) html += `<li>${inline(item)}</li>`;
    html += '</ul>';
    listBuf = [];
  };

  const flushAll = () => {
    flushPara();
    flushList();
  };

  while (i < lines.length) {
    const line = lines[i] ?? '';

    // Code fences
    const fenceMatch = /^```(\w*)\s*$/.exec(line);
    if (fenceMatch) {
      if (!inFence) {
        flushAll();
        inFence = true;
        fenceLang = fenceMatch[1] ?? '';
        html += `<pre class="code code-${escapeAttr(fenceLang || 'wine')}"><code>`;
      } else {
        inFence = false;
        html += '</code></pre>';
        fenceLang = '';
      }
      i++;
      continue;
    }
    if (inFence) {
      html += escapeHtml(line) + '\n';
      i++;
      continue;
    }

    // Blank line — flush
    if (line.trim() === '') {
      flushAll();
      i++;
      continue;
    }

    // Headings
    const h = /^(#{1,3})\s+(.+)$/.exec(line);
    if (h) {
      flushAll();
      const level = h[1]!.length;
      html += `<h${level + 1}>${inline(h[2]!.trim())}</h${level + 1}>`;
      i++;
      continue;
    }

    // Table — detect header + separator on two consecutive lines
    if (line.includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1] ?? '')) {
      flushAll();
      const header = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length) {
        const r = lines[i] ?? '';
        if (!r.includes('|') || r.trim() === '') break;
        rows.push(splitRow(r));
        i++;
      }
      html += '<table class="q-table"><thead><tr>';
      for (const h of header) html += `<th>${inline(h)}</th>`;
      html += '</tr></thead><tbody>';
      for (const r of rows) {
        html += '<tr>';
        for (const cell of r) html += `<td>${inline(cell)}</td>`;
        html += '</tr>';
      }
      html += '</tbody></table>';
      continue;
    }

    // Bulleted list
    const li = /^[-*]\s+(.*)$/.exec(line);
    if (li) {
      flushPara();
      listBuf.push(li[1] ?? '');
      i++;
      continue;
    }

    paraBuf.push(line);
    i++;
  }
  flushAll();
  if (inFence) html += '</code></pre>';
  return html;
}

function isTableSep(line: string): boolean {
  return /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line);
}

function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function inline(s: string): string {
  let out = escapeHtml(s);
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
  return out;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return s.replace(/[^\w-]/g, '');
}
